import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Check, Crown, Sparkles, Loader2 } from "lucide-react";

/*
 * Full-page subscription paywall. Shown when trial has expired OR when the
 * business clicks "Subscribe" from the trial-urgent nag banner.
 *
 * Feb 2026 tiers (matches setup_stripe.py):
 *   Foundation ($49, regular_monthly)   — invite-only, shown only to
 *                                          is_foundation users (locked-in rate).
 *   Pro         ($59, pro_monthly)       — public default for everyone.
 *   Platinum    ($99, platinum_monthly)  — public premium (crown, priority
 *                                          placement, monthly reports).
 *
 * The server verifies `is_foundation` before creating the Checkout session
 * for `regular_monthly` — no client-side plan choice can bypass that.
 */

const PLANS = [
  {
    lookupKey: "pro_monthly",
    title: "Pro",
    price: 59,
    tagline: "Standard plan — 30-day free trial",
    features: [
      "Unlimited last-minute openings",
      "Real-time booking inbox",
      "Follower push + email alerts",
      "Marketing kit + weekly hours",
      "Multi-category profile",
    ],
    variant: "pro",
    badge: "most popular",
  },
  {
    lookupKey: "platinum_monthly",
    title: "Platinum",
    price: 99,
    tagline: "Premium — priority placement + white glove",
    features: [
      "Everything in Pro",
      "Priority Map & Search placement",
      "White-Glove onboarding",
      "'Featured Business' badge",
      "Monthly Performance Reports",
    ],
    variant: "platinum",
  },
];

const FOUNDATION_PLAN = {
  lookupKey: "foundation_monthly",
  title: "Foundation Member",
  price: 49,
  tagline: "2 months free · locked-in pricing · original members only",
  features: [
    "Everything in Pro",
    "2 months free trial",
    "Locked-in $49/mo pricing for life",
    "Original member badge",
    "Direct line to the founding team",
  ],
  variant: "foundation",
};

export default function SubscribePaywall({ user: userProp, expired = true }) {
  const { user: ctxUser, refresh } = useAuth();
  const user = userProp || ctxUser;
  const [busy, setBusy] = useState(null); // lookup_key of the tier currently redirecting
  const [reconciling, setReconciling] = useState(false);
  const isFoundation = !!user?.is_foundation;
  const [seats, setSeats] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!user?.city) return;
    api
      .get("/foundation/availability", { params: { city: user.city } })
      .then(({ data }) => setSeats(data))
      .catch(() => setSeats(null));
    api
      .get("/foundation/waitlist/me")
      .then(({ data }) => setWaitlist(data?.entry || null))
      .catch(() => setWaitlist(null));
  }, [user?.city]);

  const offer = waitlist?.status === "offered" ? waitlist : null;
  const showFoundation = isFoundation || !!offer || !!seats?.is_full;
  const foundationSoldOut = !!seats?.is_full && !user?.foundation_via_invite && !offer;

  const joinWaitlist = async () => {
    setJoining(true);
    try {
      const { data } = await api.post("/foundation/waitlist/me");
      if (data?.status === "available") {
        toast.success("A Foundation spot is open — it's yours to grab now.");
        try { await refresh?.(); } catch { /* non-fatal */ }
        window.location.reload();
        return;
      }
      setWaitlist(data);
      toast.success(
        data?.status === "offered"
          ? "A spot just opened — it's held for you for 48 hours."
          : `You're #${data?.position} on the ${data?.city} waitlist.`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setJoining(false);
    }
  };

  const startCheckout = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      // Defensive: Stripe should always return a hosted-checkout URL, but if
      // the backend response is malformed / feature-flagged off, log loudly
      // and toast so the user isn't silently dumped somewhere unexpected
      // instead of onto Stripe. Never assign `undefined` to location.href.
      if (!data?.checkout_url || typeof data.checkout_url !== "string") {
        // eslint-disable-next-line no-console
        console.error("[checkout] backend returned no checkout_url:", data);
        toast.error(
          "Couldn't open Stripe Checkout — please refresh and try again. " +
            "If this keeps happening, contact hello@bookaway.com.au."
        );
        setBusy(null);
        return;
      }
      // Full redirect — Stripe hosted Checkout owns the flow from here.
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  // "I've already paid" — support fallback for the rare case where the
  // Stripe webhook crashed mid-flight and left the user stuck on the
  // paywall despite having an active subscription. Calls the reconcile
  // endpoint which looks up the user's Stripe customer and activates
  // them directly. Idempotent and safe — if they haven't actually paid,
  // it returns `activated:false` and we surface a helpful toast.
  const checkExistingSubscription = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/payments/reconcile");
      if (data?.activated) {
        toast.success(
          "Found your subscription — unlocking your dashboard…"
        );
        try { await refresh?.(); } catch { /* non-fatal */ }
        // Hard-reload so the paywall gate re-reads /auth/me on cold boot.
        window.location.assign("/dashboard");
      } else if (data?.reason === "no_stripe_customer") {
        toast.info(
          "We don't see any Stripe subscription for this account yet. Pick a plan below to get started."
        );
      } else {
        toast.info(
          "No active Stripe subscription found. If you just paid, wait a few seconds and try again — or email hello@bookaway.com.au."
        );
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconciling(false);
    }
  };

  // Foundation members see 3 tiers (their invite-only Foundation + Pro + Platinum).
  // Everyone else sees the 2 public tiers (Pro + Platinum).
  const tiers = showFoundation ? [FOUNDATION_PLAN, ...PLANS] : PLANS;
  const gridCls = tiers.length === 3
    ? "md:grid-cols-3"
    : "md:grid-cols-2";

  return (
    <div className="mx-auto max-w-[1080px] p-6 md:p-12" data-testid="dashboard-trial-lockout">
      <div className="inline-flex items-center gap-2 border border-[#C9A85F] px-3 py-1.5">
        <Lock size={12} className="text-[#C9A85F]" />
        <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
          {expired ? "trial ended" : "choose your plan"}
        </span>
      </div>
      <h1 className="mt-6 text-4xl leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
        {expired ? "Keep filling last-minute slots." : "Ready to go pro?"}
      </h1>
      <p className="slogan gold-metallic-text mt-4 text-2xl md:text-3xl">
        Subscribe to unlock your full dashboard.
      </p>
      <p className="mt-6 max-w-xl text-sm text-muted-foreground">
        Cancel anytime · no lock-in · billed monthly · secure checkout via
        Stripe.
        {expired
          ? " Your existing openings and past requests are still safely on your account."
          : " Pick your tier — you'll be back here in seconds."}
      </p>

      <div className={`mt-10 grid gap-4 ${gridCls}`}>
        {tiers.map((plan) => (
          <TierCard
            key={plan.lookupKey}
            testId={`subscribe-tier-${plan.variant}`}
            plan={plan}
            busy={busy === plan.lookupKey}
            disabled={
              (!!busy && busy !== plan.lookupKey) ||
              (plan.variant === "foundation" && foundationSoldOut)
            }
            soldOut={plan.variant === "foundation" && foundationSoldOut}
            seats={plan.variant === "foundation" ? seats : null}
            offer={plan.variant === "foundation" ? offer : null}
            inviteBypass={plan.variant === "foundation" && !!user?.foundation_via_invite}
            onClick={() => startCheckout(plan.lookupKey)}
          />
        ))}
      </div>

      {foundationSoldOut && (
        <WaitlistPanel city={seats?.city} entry={waitlist} joining={joining} onJoin={joinWaitlist} />
      )}

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <button
          type="button"
          data-testid="paywall-check-existing-sub"
          onClick={checkExistingSubscription}
          disabled={reconciling || !!busy}
          className="mono text-[10px] uppercase tracking-widest border border-border px-4 py-2 text-muted-foreground hover:border-[#C9A85F] hover:text-[#8A7333] transition-colors disabled:opacity-50"
        >
          {reconciling ? "Checking Stripe…" : "I've already paid — check my subscription"}
        </button>
        <Link
          to="/"
          data-testid="dashboard-back-to-marketplace"
          className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          ← back to marketplace
        </Link>
      </div>
    </div>
  );
}

function WaitlistPanel({ city, entry, joining, onJoin }) {
  return (
    <div
      data-testid="foundation-waitlist-panel"
      className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-[#C9A85F] bg-[#C9A85F]/5 p-5"
    >
      <div>
        <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#8A7333]">
          Foundation waitlist · {city}
        </p>
        <p data-testid="foundation-waitlist-status" className="mt-2 text-sm">
          {entry?.status === "waiting"
            ? `You're #${entry.position} in line. We'll email you the moment a spot opens and hold it for you for 48 hours.`
            : "All 20 spots are taken. Join the waitlist and we'll email you when one opens up — held for you for 48 hours."}
        </p>
      </div>
      {entry?.status !== "waiting" && (
        <button
          type="button"
          data-testid="foundation-waitlist-join-btn"
          onClick={onJoin}
          disabled={joining}
          className="gold-metallic-bg inline-flex h-11 items-center gap-2 px-5 text-xs font-semibold uppercase tracking-widest text-[#2A2724] disabled:opacity-60"
        >
          {joining && <Loader2 size={14} className="animate-spin" />}
          Join the waitlist
        </button>
      )}
    </div>
  );
}

function TierCard({ testId, plan, busy, disabled, soldOut, seats, offer, inviteBypass, onClick }) {
  const { title, price, tagline, features, variant, badge } = plan;
  const isFoundation = variant === "foundation";
  const isPro = variant === "pro";
  const isPlatinum = variant === "platinum";

  const containerCls = isFoundation
    ? "border border-[#C9A85F] bg-[#C9A85F]/5 hover:bg-[#C9A85F]/10"
    : isPro
    ? "border-2 border-[#C9A85F] bg-[#C9A85F]/[0.03] hover:bg-[#C9A85F]/[0.06]"
    : isPlatinum
    ? "border border-border hover:border-foreground"
    : "border border-border hover:border-foreground";

  const ctaCls = isPro || isPlatinum || isFoundation
    ? "gold-metallic-bg text-[#2A2724]"
    : "border border-foreground text-foreground";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      data-testid={testId}
      className={`text-left p-6 md:p-8 transition-all relative ${containerCls} ${
        busy ? "opacity-70 cursor-wait" : soldOut ? "opacity-60 cursor-not-allowed grayscale" : disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer active:scale-[0.99]"
      }`}
    >
      {badge && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-1 bg-[#C9A85F]/20 border border-[#C9A85F]">
          <Sparkles size={10} className="text-[#C9A85F]" />
          <span className="mono text-[9px] uppercase tracking-widest text-[#8A7333]">
            {badge}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
          {(isFoundation || isPlatinum) && (
            <Crown size={12} className="text-[#C9A85F]" fill="currentColor" />
          )}
          {title}
        </p>
      </div>
      <p className="mt-4 text-4xl md:text-5xl leading-none tracking-tight">
        <span className="wordmark gold-metallic-text">${price}</span>
        <span className="mono ml-2 text-xs uppercase tracking-widest text-muted-foreground">
          / mo AUD
        </span>
      </p>
      <p className="mt-3 text-sm text-muted-foreground">{tagline}</p>
      {seats && (
        <p
          data-testid="foundation-seats-counter"
          className={`mono mt-3 text-[10px] uppercase tracking-widest ${
            soldOut ? "text-destructive" : "text-[#8A7333]"
          }`}
        >
          {offer
            ? `Spot held for you until ${new Date(offer.offer_expires_at).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`
            : seats.is_full && inviteBypass
            ? `Sold out in ${seats.city} · your invite code reserves your spot`
            : `${seats.seats_remaining} of ${seats.seats_total} Foundation spots left in ${seats.city}`}
        </p>
      )}
      <ul className="mt-5 grid gap-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs">
            <Check size={12} className="mt-0.5 text-[#C9A85F] flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div
        className={`mt-6 inline-flex h-11 items-center gap-2 px-5 text-xs font-semibold uppercase tracking-widest ${ctaCls}`}
      >
        {soldOut ? (
          <span data-testid="foundation-sold-out-label">Sold out in your city</span>
        ) : busy ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Redirecting…
          </>
        ) : (
          <>Subscribe · ${price}/mo</>
        )}
      </div>
    </button>
  );
}
